import type { CSSProperties } from 'react';
import { SportIDs } from 'types/enums';

export const TOAST_ERROR_STYLE = {
  color: '#d8000c',
  backgroundColor: '#ffbaba',
};

/** For confirmations: the container paints every toast as an error. */
export const TOAST_SUCCESS_STYLE = {
  color: '#0f5132',
  backgroundColor: '#c8f0d6',
  // Read by the modern layout's toast (themes/modern.css).
  '--toast-accent': 'var(--theme-positive)',
  '--toast-glyph': '"✓"',
} as CSSProperties;

export const ALPHA_NUMERIC_WITH_SPACE_REGEX = /^[a-zA-Z0-9 ]+$/g;

export const VALIDATION_EMAIL_REGEX: RegExp =
  /^[\w][\w._%+-]*[\w]@[\w][\w.-]*[\w]\.[a-zA-Z]{2,}$/i;

export const TWO_WORDS_REGEX = /\w+\s\w+/g;

export const TOAST_AUTO_CLOSE_TIME = 1_500; // 1.5 seconds

export const TOOLTIP_DELAY_SHOW = 1_000; // 1 second

export const HIGHLIGHT_ODDS_CHANGE_TIME = 10_000; // 10 seconds

export const WEB_SOCKET_PING_INTERVAL = 12_000; //12 seconds

export const ACCESS_TOKEN_REFRESH_INTERVAL = 240_000; // 4 minutes

export const WEB_SOCKET_PING_MESSAGE = JSON.stringify({
  message: 'Hello, I am still here.',
});

export const HOME_PHOTO_INTERVAL = 10_000; // 10 seconds

export const AXIOS_CACHE_TTL = 60_000; // 1 minute

export const GAME_API_CACHE_TTL = 300_000; // 5 minutes

/** Minimum time the catalog loader stays visible (avoids flash on fast/cached loads). */
export const CASINO_CATALOG_MIN_LOADER_MS = 200;

export const TIME_FILTER_VALUES = [
  'today_label',
  '3_hours_label',
  '24_hours_label',
  '3_days_label',
  'long_term_label',
];

export const START_YEAR = 2026; // Before 2026 there can not be any bets

export const FULL_TIME_BETGROUP_NAME = 'Full Time';
/**
 * The market whose winner odds fill a prematch row's 1 / X / 2, per sport id,
 * first one present wins. Taken from what the feed actually sends in each
 * sport's "main" group; only markets the server can settle are listed (MMA's
 * "Fight Lines" is not, so MMA falls back to "To Win Fight" when offered).
 */
export const MAIN_MARKETS_BY_SPORT = new Map<number, string[]>([
  [1, ['Full Time Result']], // soccer
  [13, ['To Win Match']], // tennis
  [17, ['3-Way', 'Game Lines']], // ice hockey
  [18, ['Game Lines']], // basketball
  [78, ['Game Lines 3-Way', 'Game Lines']], // handball
  [91, ['Game Lines']], // volleyball
  [92, ['Match Lines']], // table tennis
  [94, ['Match Lines']], // badminton
  [151, ['Match Lines']], // e-sports
  [12, ['Game Lines']], // american football
  [16, ['Game Lines']], // baseball
  [36, ['Game Lines']], // australian rules
  [9, ['To Win Fight']], // boxing
  [162, ['To Win Fight']], // mma
  [3, ['To Win the Match', 'To Win Match']], // cricket
  [15, ['Match Winner']], // darts
  [14, ['Match Winner', 'To Win Match']], // snooker
  [107, ['Match Winner']], // squash
  [90, ['3-Way Betting']], // floorball
  [75, ['Game Betting 3-Way', 'Game Betting 2-Way']], // gaelic sports
  [19, ['Game Betting 3-Way', 'Game Betting 2-Way']], // rugby league
  [8, ['Game Betting 3-Way', 'Game Betting 2-Way']], // rugby union
  [110, ['3-Way', 'Game Lines']], // water polo
  [83, ['Full Time Result', '3-Way']], // futsal
]);

export const LIVE_FULL_TIME_BET_MAP = new Map<SportIDs, string>([
  [SportIDs.SOCCER, 'Fulltime Result'],
  [SportIDs.TENNIS, 'To Win'],
  [SportIDs.BASKETBALL, 'Game Lines'],
]);

export const SPORT_NAMES_MAP = new Map<SportIDs, string>([
  [SportIDs.SOCCER, 'Soccer'],
  [SportIDs.TENNIS, 'Tennis'],
  [SportIDs.ICE_HOCKEY, 'Ice Hockey'],
  [SportIDs.BASKETBALL, 'Basketball'],
  [SportIDs.VOLLEYBALL, 'Volleyball'],
  [SportIDs.HANDBALL, 'Handball'],
]);

export const LIVE_BETTING_SPORT_HEADER_LABELS = new Map<number, string[]>([
  [1, ['3_way', 'match_remaining', 'next_goal', 'over_under']],
  [13, ['2_way', 'set_winner', 'set_result', 'over_under']],
  [17, ['2_way', '', '', '']],
  [18, ['3_way', '', 'handicap', 'over_under']],
  [78, ['2_way', '', '', '']],
  [91, ['2_way', '', '', '']],
]);

export const DEFAULT_TOTAL_STAKE = '2.00';
export const DEFAULT_MIN_STAKE = '5.00';
export const DEFAULT_MAX_STAKE = '1000.00';
export const DEFAULT_MIN_STAKE_PER_BET = '0.1';

/**
 * The most one ticket pays (the server's MAX_WIN_FOR_TICKET). A slip whose odds
 * would pay more is still played, with this as its potential win.
 */
export const MAX_WIN_FOR_TICKET = Number(
  process.env.REACT_APP_MAX_WIN_FOR_TICKET ?? 30000
);

/** Closed bets open on this many days back, the filter pre-set to match. */
export const CLOSED_BETS_DEFAULT_DAYS = 7;

export const LIVE_BETTING_REFRESH_INTERVAL = 5_000; // 5 seconds

/** How often an open slip asks for its live prices. */
export const SLIP_QUOTES_INTERVAL = 5_000;

/** Most matches on one ticket (owner, 2026-11-03: Forzza 30, Sporttipp 15).
 *  More picks on a match already on it are still allowed (combinations). */
export const SLIP_MAX_MATCHES = 25;

const parsedLiveTicketWaitMs = Number.parseInt(
  process.env.REACT_APP_LIVE_TICKET_WAIT_MS ?? '8000',
  10
);

/**
 * The count shown while a slip is checked (ms). The wait itself is the
 * server's: `verify_live_quotes` asks BetsAPI for 5 s from the click on a slip
 * with a live pick (bets/slip_window.go in the API). Keep the two equal.
 * From `.env`: `REACT_APP_LIVE_TICKET_WAIT_MS`.
 */
export const LIVE_TICKET_WAIT_MS =
  Number.isFinite(parsedLiveTicketWaitMs) && parsedLiveTicketWaitMs >= 0
    ? parsedLiveTicketWaitMs
    : 8_000;

/** sessionStorage: “Accept odds change” for the bet slip; survives clearing selections. */
export const SLIP_ACCEPT_ODD_CHANGES_SESSION_KEY = 'slipAcceptOddChanges';

/** Live sports, in the order the live pages list them: football, basketball, tennis first. */
export const LIVE_SPORT_IDs = [
  1, 18, 13, 17, 91, 78, 92, 151, 14, 15, 16, 12, 8, 19, 36, 83, 110, 95, 94,
  107, 90, 75, 66, 2, 3, 4, 9, 162,
];

/** English labels for each `sport_id` used on live-betting (must match backend). */
export const LIVE_SPORT_DISPLAY_NAME_MAP = new Map<number, string>([
  [1, 'Soccer'],
  [2, 'Horse Racing'],
  [3, 'Cricket'],
  [4, 'Greyhounds'],
  [8, 'Rugby Union'],
  [9, 'Boxing'],
  [12, 'American Football'],
  [13, 'Tennis'],
  [14, 'Snooker'],
  [15, 'Darts'],
  [16, 'Baseball'],
  [17, 'Ice Hockey'],
  [18, 'Basketball'],
  [19, 'Rugby League'],
  [36, 'Australian Rules'],
  [66, 'Bowls'],
  [75, 'Gaelic Sports'],
  [78, 'Handball'],
  [83, 'Futsal'],
  [90, 'Floorball'],
  [91, 'Volleyball'],
  [92, 'Table Tennis'],
  [94, 'Badminton'],
  [95, 'Beach Volleyball'],
  [107, 'Squash'],
  [110, 'Water Polo'],
  [151, 'Esports'],
  [162, 'MMA'],
]);

export function getLiveBettingSportName(sportId: number): string {
  const explicit = LIVE_SPORT_DISPLAY_NAME_MAP.get(sportId);
  if (explicit) {
    return explicit;
  }
  const legacy = SPORT_NAMES_MAP.get(sportId as SportIDs);
  if (legacy) {
    return legacy;
  }
  return `Sport ${sportId}`;
}

export const FORZZA_DESKTOP_RESULTS_URL = 'https://forzza1.com/en/results';
export const FORZZA_MOBILE_RESULTS_URL = 'https://m.forzza1.com/#results';

export const ROUTE_TITLE_MAP = new Map<String, String>([
  ['/casino-games', 'casino_games_label'],
  ['/', 'home_label'],
  ['/sports-betting', 'sports_betting_label'],
  ['/live-casino', 'live_casino_label'],
  ['/lucky-six', 'lucky_six_label'],
  ['/keno', 'keno_label'],
  ['/live-betting', 'live_betting_label'],
  ['/login', 'login_label'],
  ['/highlights', 'highlights_label'],
  ['/evo-auto-games', 'evo_auto_games_label'],
  ['/results', 'results_label'],
  ['/search', 'search_label'],
  ['/contact', 'contact_label'],
  ['/language', 'language_label'],
  ['/info', 'Info'],
  ['/slip', 'slip_label'],
  ['/deposit', 'deposit_label'],
  ['/account-settings', 'account_settings_label'],
  ['/theme-settings', 'theme_settings_label'],
  ['/change-password', 'change_password_label'],
  ['/betting-limits', 'betting_limits_label'],
  ['/account-lock', 'account_lock_label'],
  ['/personal-data', 'personal_data_label'],
  ['/change-email', 'change_email_label'],
  ['/my-bonuses', 'my_bonuses_label'],
]);

export const WORK_IN_PROGRESS_PAGES = [
  '/responsible-gaming',
  '/highlights',
  '/evo-auto-games',
  '/about-us',
  '/terms-and-conditions',
  '/privacy-policy',
  '/anti-money-laundering',
  '/responsible-gaming',
  '/become-affiliate',
];

/** The pitch in a live football match's Live tab: 'betzone' is the BetZone
 *  tracker widget (its match id from SportAPI, paired on the server), and a
 *  match it does not cover falls back to our own drawn pitch; 'pitch' is our
 *  own pitch only (the old one). To go back: REACT_APP_LIVE_TRACKER=pitch in
 *  .env.prod, or change the default here, and rebuild. */
export const LIVE_TRACKER: 'betzone' | 'pitch' =
  process.env.REACT_APP_LIVE_TRACKER === 'pitch' ? 'pitch' : 'betzone';

/** BetZone tracker view: '3d' (default) or '2d' (REACT_APP_LIVE_TRACKER_VIEW=2d). */
export const BETZONE_VIEW: '2d' | '3d' =
  process.env.REACT_APP_LIVE_TRACKER_VIEW === '2d' ? '2d' : '3d';

/** BetZone's embed key; the widget answers only on play.tottalo.com. */
export const BETZONE_KEY = 'pk_tottalo';
